/**
 * Politique d'accès aux pages pour un compte "member" du BO Contrôle.
 *
 * Source de vérité unique (guard MemberPageGuard + AppComponent) pour décider où
 * un member peut aller, afin qu'on ne puisse pas réintroduire la boucle de
 * redirection du ticket #4965. Inclut le lecteur de permissions (localStorage)
 * pour éviter de dupliquer le parsing à trois endroits.
 */

// Routes utilitaires toujours accessibles à un member, même sans aucune page
// attribuée. /mes-informations est la page neutre de repli (jamais protégée par
// enablePages), ce qui en fait une cible de redirection SÛRE (non bouclante).
//
// NB: actuellement le MemberPageGuard n'est branché que sur des routes protégées
// (tableau-de-bord, liste-des-participants, configurer-*, gerer-les-acces,
// parametres) — aucune n'est dans cette liste. La branche always-allowed de
// resolveMemberRoute est donc défensive (si le guard est un jour ajouté sur
// aide/support/mes-informations) ; son rôle effectif aujourd'hui est de
// documenter les cibles de redirection sûres.
export const MEMBER_ALWAYS_ALLOWED = ['redirect', 'redirect-to-bo-online', 'deconnecte', 'aide', 'support', 'mes-informations'];

/** Forme de localStorage.memberPermissions. enableList vide = toutes les listes. */
export interface MemberPermissions {
  enableList: string[];
  enablePages: string[];
  /** Présent uniquement si l'accès est refusé (403 STAFF_ACCESS_REVOKED, #8627). */
  accessDenied?: true;
}

export interface MemberRouteDecision {
  allowed: boolean;
  // Garanti présent et non bouclant quand allowed === false. NB: pas une union
  // discriminée car le projet n'est pas en mode strict → TS ne narrow pas
  // {allowed:true}|{allowed:false} (TS2339 au build), redirectTo reste optionnel.
  redirectTo?: string;
}

/**
 * Lit les pages autorisées d'un member depuis localStorage.memberPermissions.
 * Réservoir unique du parsing (utilisé par le guard et AppComponent) — fail-safe :
 * une entrée illisible donne [] avec un warning diagnostique.
 */
export function readMemberEnablePages(): string[] {
  const permsRaw = localStorage.getItem('memberPermissions');
  if (!permsRaw) return [];
  try {
    return JSON.parse(permsRaw).enablePages || [];
  } catch (e) {
    console.warn('memberPermissions illisible (JSON invalide), enablePages traité comme vide', e);
    return [];
  }
}

/**
 * Page d'atterrissage d'un member : sa 1re page autorisée, sinon la page neutre.
 * Ne renvoie JAMAIS une route protégée arbitraire (ex. /tableau-de-bord) en
 * l'absence de pages — sinon le guard boucle (#4965).
 */
export function memberLandingUrl(enablePages: string[]): string {
  return enablePages.length > 0 ? '/' + enablePages[0] : '/mes-informations';
}

/**
 * Décide si un member peut activer `currentPath`. Si non, fournit une cible de
 * redirection garantie non bouclante (page autorisée ou page neutre) — jamais la
 * route protégée courante elle-même.
 */
export function resolveMemberRoute(currentPath: string, enablePages: string[]): MemberRouteDecision {
  if (MEMBER_ALWAYS_ALLOWED.includes(currentPath)) return {allowed: true};
  if (enablePages.includes(currentPath)) return {allowed: true};
  return {allowed: false, redirectTo: memberLandingUrl(enablePages)};
}
